import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Plus, Trash2, Loader2, Save, X, UserPlus } from 'lucide-react';

const API_URL = window.location.origin;
const authHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

const CircleRow = ({ circle, legacyMembers, onRefresh }) => {
  const [edit, setEdit] = useState({ name: circle.name, zoom_link: circle.zoom_link || '', meeting_rhythm: circle.meeting_rhythm || '' });
  const [expanded, setExpanded] = useState(false);
  const [addUserId, setAddUserId] = useState('');

  const memberIds = new Set((circle.members || []).map(m => m.id));
  const available = legacyMembers.filter(m => !memberIds.has(m.id));

  const handleSave = async () => {
    try {
      await axios.put(`${API_URL}/api/admin/legacy/witness-circles/${circle.id}`, edit, authHeaders());
      toast.success('Circle updated');
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update');
    }
  };

  const handleAddMember = async () => {
    if (!addUserId) return;
    try {
      const res = await axios.post(`${API_URL}/api/admin/legacy/witness-circles/${circle.id}/members`, { user_id: addUserId }, authHeaders());
      toast.success(res.data.message);
      setAddUserId('');
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add member');
    }
  };

  const handleRemoveMember = async (m) => {
    try {
      await axios.delete(`${API_URL}/api/admin/legacy/witness-circles/${circle.id}/members/${m.id}`, authHeaders());
      onRefresh();
    } catch { toast.error('Failed to remove'); }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Delete "${circle.name}"? Its private posts will also be deleted.`)) return;
    try {
      await axios.delete(`${API_URL}/api/admin/legacy/witness-circles/${circle.id}`, authHeaders());
      toast.success('Circle deleted');
      onRefresh();
    } catch { toast.error('Failed to delete'); }
  };

  return (
    <div className="border border-sand rounded-xl p-4 space-y-3" data-testid={`admin-wc-${circle.id}`}>
      <div className="flex items-center justify-between gap-2">
        <button onClick={() => setExpanded(!expanded)} className="flex items-center gap-2 text-left min-w-0" data-testid={`admin-wc-toggle-${circle.id}`}>
          <p className="font-medium text-forest truncate">{circle.name}</p>
          <Badge variant="outline" className="text-xs">{(circle.members || []).length} members</Badge>
          <span className="text-xs text-terracotta whitespace-nowrap">{expanded ? 'Hide details ▲' : 'Edit details ▼'}</span>
        </button>
        <Button variant="ghost" size="sm" onClick={handleDelete} data-testid={`admin-wc-delete-${circle.id}`}>
          <Trash2 className="h-4 w-4 text-red-400" />
        </Button>
      </div>
      <div className="flex flex-wrap gap-1.5">
        {(circle.members || []).map(m => (
          <span key={m.id} className="inline-flex items-center gap-1 text-xs bg-sand/70 rounded-full px-2 py-1 text-gray-700">
            {m.full_name}
            <button onClick={() => handleRemoveMember(m)} data-testid={`admin-wc-remove-${circle.id}-${m.id}`}><X className="h-3 w-3 text-red-400" /></button>
          </span>
        ))}
        {(circle.members || []).length === 0 && <span className="text-xs text-gray-400">No members placed yet</span>}
      </div>
      <div className="flex gap-2">
        <Select value={addUserId} onValueChange={setAddUserId}>
          <SelectTrigger className="flex-1" data-testid={`admin-wc-member-select-${circle.id}`}>
            <SelectValue placeholder="Place a member in this circle..." />
          </SelectTrigger>
          <SelectContent>
            {available.map(m => <SelectItem key={m.id} value={m.id}>{m.full_name || m.email}</SelectItem>)}
            {available.length === 0 && <div className="px-3 py-2 text-xs text-gray-400">All Legacy members are already placed</div>}
          </SelectContent>
        </Select>
        <Button size="sm" onClick={handleAddMember} disabled={!addUserId} className="bg-forest hover:bg-forest/90 text-white" data-testid={`admin-wc-add-member-${circle.id}`}>
          <UserPlus className="h-4 w-4 mr-1" /> Add
        </Button>
      </div>
      {expanded && (
        <div className="space-y-3 pt-2 border-t border-sand">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            <Input value={edit.name} onChange={(e) => setEdit({ ...edit, name: e.target.value })} placeholder="Circle name" />
            <Input value={edit.zoom_link} onChange={(e) => setEdit({ ...edit, zoom_link: e.target.value })} placeholder="Zoom link" />
            <Input value={edit.meeting_rhythm} onChange={(e) => setEdit({ ...edit, meeting_rhythm: e.target.value })} placeholder="e.g., 2nd Tuesday · 7 PM ET" />
          </div>
          <Button size="sm" variant="outline" onClick={handleSave} data-testid={`admin-wc-save-${circle.id}`}>
            <Save className="h-4 w-4 mr-2" /> Save Details
          </Button>
        </div>
      )}
    </div>
  );
};

export const WitnessCirclesAdmin = ({ legacyMembers }) => {
  const [circles, setCircles] = useState([]);
  const [newName, setNewName] = useState('');
  const [creating, setCreating] = useState(false);

  const fetchCircles = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/legacy/witness-circles`, authHeaders());
      setCircles(res.data);
    } catch { /* noop */ }
  };
  useEffect(() => { fetchCircles(); }, []);

  const handleCreate = async () => {
    if (!newName.trim()) { toast.error('Circle name is required'); return; }
    setCreating(true);
    try {
      await axios.post(`${API_URL}/api/admin/legacy/witness-circles`, { name: newName }, authHeaders());
      toast.success('Witness Circle created');
      setNewName('');
      fetchCircles();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create');
    } finally {
      setCreating(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg font-heading text-forest">Witness Circles</CardTitle>
        <p className="text-sm text-gray-500">Small groups of 3–5 sisters. Each member belongs to one circle; placing her in a new one moves her automatically. Members see a private feed, Zoom link, and meeting rhythm.</p>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex gap-2">
          <Input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder='New circle name — e.g., "Circle of Ruth"' data-testid="admin-wc-name-input" />
          <Button onClick={handleCreate} disabled={creating} className="bg-forest hover:bg-forest/90 text-white" data-testid="admin-wc-create-btn">
            {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
          </Button>
        </div>
        {circles.map(c => <CircleRow key={c.id} circle={c} legacyMembers={legacyMembers} onRefresh={fetchCircles} />)}
        {circles.length === 0 && <p className="text-sm text-gray-400 text-center py-2">No Witness Circles yet — create the first one above.</p>}
      </CardContent>
    </Card>
  );
};

export const SeasonsAdmin = () => {
  const [seasons, setSeasons] = useState([]);
  const [selected, setSelected] = useState('1');
  const [saving, setSaving] = useState(false);

  const fetchSeasons = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/legacy/seasons`, authHeaders());
      setSeasons(res.data.seasons);
    } catch { /* noop */ }
  };
  useEffect(() => { fetchSeasons(); }, []);

  const season = seasons.find(s => String(s.season) === selected);
  const update = (field, value) => setSeasons(seasons.map(s => String(s.season) === selected ? { ...s, [field]: value } : s));

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/admin/legacy/seasons/${selected}`, {
        description: season.description, invitation: season.invitation,
        practices: season.practices, video_url: season.video_url
      }, authHeaders());
      toast.success(`Season ${selected} content saved`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  if (!season) return null;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg font-heading text-forest">Season Content</CardTitle>
        <p className="text-sm text-gray-500">The words members see for each season of the journey (shown on the Welcome tab and Home). Set the active season in Space Settings above.</p>
      </CardHeader>
      <CardContent className="space-y-3">
        <Select value={selected} onValueChange={setSelected}>
          <SelectTrigger data-testid="admin-season-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            {seasons.map(s => <SelectItem key={s.season} value={String(s.season)}>Season {s.season}: {s.name} ({s.months})</SelectItem>)}
          </SelectContent>
        </Select>
        <p className="text-sm text-terracotta italic">{season.question}</p>
        <div>
          <Label className="text-xs">Description</Label>
          <Textarea value={season.description} onChange={(e) => update('description', e.target.value)} rows={2} data-testid="admin-season-description-input" />
        </div>
        <div>
          <Label className="text-xs">Invitation (what this season asks of her)</Label>
          <Textarea value={season.invitation} onChange={(e) => update('invitation', e.target.value)} rows={2} data-testid="admin-season-invitation-input" />
        </div>
        <div>
          <Label className="text-xs">Practices</Label>
          <Textarea value={season.practices} onChange={(e) => update('practices', e.target.value)} rows={2} data-testid="admin-season-practices-input" />
        </div>
        <div>
          <Label className="text-xs">Season Video URL (optional, embed link)</Label>
          <Input value={season.video_url || ''} onChange={(e) => update('video_url', e.target.value)} placeholder="https://player.vimeo.com/video/..." />
        </div>
        <Button onClick={handleSave} disabled={saving} className="bg-forest hover:bg-forest/90 text-white" data-testid="admin-season-save-btn">
          {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />} Save Season {selected}
        </Button>
      </CardContent>
    </Card>
  );
};
